import React from 'react';
import { ForgeList } from '@tylertech/forge-react';
import cx from 'classnames';
import { UniqueIdentifier } from '@dnd-kit/core';
import { DragDropContainerType, DragDropElementWrapper } from './index';

interface ContainerRendererProps<T = unknown> {
  type: DragDropContainerType;
  items: DragDropElementWrapper<T>[];
  className?: string;
  iconName?: string;
  dense?: boolean;
  activeId: UniqueIdentifier | null;
  tableHeadContent?: React.ReactNode;
  SortableComponent: React.ComponentType<{
    item: DragDropElementWrapper<T>;
    isDragOverlay?: boolean;
    iconName?: string;
  }>;
}

// Get the appropriate container element based on type
const getContainerElement = (type: DragDropContainerType) => {
  switch (type) {
    case DragDropContainerType.TABLE:
      return 'tbody';
    case DragDropContainerType.LIST:
      return 'ul';
    case DragDropContainerType.FORGE_LIST:
      return ForgeList;
    case DragDropContainerType.CONDITIONS:
    case DragDropContainerType.FORGE_CARD:
      return 'div';
    default:
      return 'div';
  }
};

export const ContainerRenderer = <T = unknown,>({
  type,
  items,
  className,
  iconName,
  dense = false,
  activeId,
  tableHeadContent,
  SortableComponent,
}: ContainerRendererProps<T>) => {
  const Container = getContainerElement(type);
  const isTable = type === DragDropContainerType.TABLE;

  const containerProps = {
    className: cx('dnd-kit-container', className, {
      'drag-active': activeId !== null,
      'sortable-forge-list': type === DragDropContainerType.FORGE_LIST,
    }),
    ...(type === DragDropContainerType.FORGE_LIST && { dense: dense.toString() }),
  };

  const containerContent = (
    <Container {...containerProps}>
      {items.map((item) => (
        <SortableComponent key={item.id} item={item} iconName={iconName} />
      ))}
    </Container>
  );

  if (isTable) {
    return (
      <table>
        {tableHeadContent}
        {containerContent}
      </table>
    );
  }

  return containerContent;
};
